import { ref, watch, nextTick, onUnmounted, unref, type Ref, type MaybeRef } from 'vue';

const PROBE_INNER_SELECTOR = '.side-nav__desktop-bar-inner--probe';

/**
 * Compares visible desktop bar width to a hidden probe row; sets `desktopNeedsBurger` when links overflow.
 */
export function useDesktopNavOverflow(
  isWideScreen: Ref<boolean>,
  desktopBarInnerRef: Ref<HTMLElement | null>,
  desktopRowProbeRef: MaybeRef<HTMLElement | null>,
  layoutDeps: () => readonly unknown[]
) {
  const desktopNeedsBurger = ref(false);
  let desktopLayoutObserver: ResizeObserver | null = null;

  function checkDesktopOverflow(): void {
    if (!isWideScreen.value) {
      desktopNeedsBurger.value = false;
      return;
    }
    const inner = desktopBarInnerRef.value;
    const probeRoot = unref(desktopRowProbeRef);
    const probe = probeRoot?.querySelector(PROBE_INNER_SELECTOR) as HTMLElement | null;
    if (!inner || !probe) return;
    desktopNeedsBurger.value = probe.scrollWidth > inner.clientWidth + 1;
  }

  function setupDesktopLayoutObservers(): void {
    desktopLayoutObserver?.disconnect();
    if (!isWideScreen.value) return;
    const inner = desktopBarInnerRef.value;
    const probe = unref(desktopRowProbeRef);
    if (!inner) return;
    if (!desktopLayoutObserver) {
      desktopLayoutObserver = new ResizeObserver(() => {
        requestAnimationFrame(checkDesktopOverflow);
      });
    }
    desktopLayoutObserver.observe(inner);
    if (probe) {
      desktopLayoutObserver.observe(probe);
    }
  }

  watch(
    () => layoutDeps(),
    () => {
      nextTick(() => {
        checkDesktopOverflow();
        setupDesktopLayoutObservers();
      });
    },
    { flush: 'post' }
  );

  onUnmounted(() => {
    desktopLayoutObserver?.disconnect();
    desktopLayoutObserver = null;
  });

  return {
    desktopNeedsBurger,
    checkDesktopOverflow,
    setupDesktopLayoutObservers,
  };
}
